// FLOW-01 (task 5.3, D15) — o canvas edita o MESMO documento que o código: o YAML é a fonte de
// verdade, e cada edição do canvas (inserir, excluir, reordenar ramo, trocar um campo) devolve o
// texto novo, que a página trata como se a pessoa o tivesse digitado. Correção de 14/10: o "mover
// para cima/baixo" saiu do menu do nó, e o `moverPasso` com ele; a troca de lugar (`trocarItens`)
// ficou, porque é ela que reordena os ramos do Router.
//
// O documento é lido pela Document API do `yaml` (eemeli): é ela que diz onde cada passo começa e
// acaba e que escreve o passo novo. Mas a reescrita é CIRÚRGICA, só no trecho do passo: o
// `doc.toString()` inteiro desloca os comentários de fim de linha (`params:   # o que o modelo
// preenche` vira um comentário na linha de baixo) e desfaz o alinhamento que a pessoa escolheu, num
// arquivo que ela está lendo ao lado. Por isso só as listas em estilo fluxo (`then: [ ... ]`), que
// não têm linha por passo, caem no `toString()`.
//
// Os comentários seguem o passo: os que estão logo acima de um item andam com ele quando ele muda
// de lugar (o PLAN P12 media que o `toString()` os perdia ao reordenar), e excluir um passo não leva
// o comentário de ninguém — nem o de cima dele, que passa a ser do passo seguinte.
import {
  isMap,
  isScalar,
  isSeq,
  LineCounter,
  parseDocument,
  Scalar,
  stringify,
} from 'yaml';
import { documentoLido } from './leituraYaml';
import { aplicarTrocas, trocasNosPassos } from './nomesNasFormulas';
import { curlParaPassoHttp } from './curlParaPassoHttp';

// O `id` que o compilador aceita e mostra no caminho (`compilador.py`, `_ID`).
export const ID_VALIDO = /^[a-z][a-z0-9_]*$/;

// O esqueleto do flow novo (o editor abre com ele): compila de primeira, para quem chega ver o
// editor verde e o formato. Mora aqui, e não na página, para o teste partir do mesmo texto que a
// pessoa vê ao criar um flow. FLOW-11 (S7): sem `efeito`, que o PowerFlow deduz dos passos.
// FLOW-03b (aceite L5, D1): o flow novo ainda não escolheu o gatilho, e o modelo não supõe um
// agente. O parâmetro obrigatório de antes impedia publicar como Agenda, que não o preenche, e um
// opcional usado no `return` falharia a cada batida. O exemplo de parâmetro fica no modelo do flow
// que vem de um agente.
export const MODELO_DO_FLOW = `name: novo_flow
description: O que este flow faz.

steps:
  - type: return
    value: { ok: true }
`;

// FLOW-03b (Task 3.7; PLAN P13): o flow novo que vem de um agente (`?agente=`) já nasce de Agente,
// porque quem o cria dali quer uma tool. O que nasce fora de um agente fica sem gatilho, e o
// "Publicar" pede um (Q6).
export const MODELO_DO_FLOW_DO_AGENTE = `name: novo_flow
description: O que o modelo lê para decidir chamar esta tool.

gatilho:
  agente: {}

params:
  exemplo: { type: string, description: Um parâmetro que o modelo preenche }

steps:
  - type: return
    value: { recebido: "{{ params.exemplo }}" }
`;

// FLOW-13 (SPEC S4, R8; PLAN G12): o esqueleto da ferramenta HTTP nova (a rota
// `powerflow_ferramenta_http_nova`), com a forma do R8 (`F/formaDaFerramenta.js`): os `params`, um
// passo `http` e um `return`. É o exemplo do S4 sem o segredo, para compilar de primeira numa conta
// de cofre vazio; a Autenticação o põe pelo segredo escolhido.
export const MODELO_DA_FERRAMENTA_HTTP = `name: consultar_pedido
description: Consulta um pedido no CRM pelo número que o cliente informou.
params:
  numero:
    type: string
    description: Número do pedido, só dígitos
    example: "48213"
steps:
  - id: chamada
    type: http
    method: GET
    url: "https://api.crm.exemplo.com/pedidos/{{ params.numero }}"
  - type: return
    value: "{{ chamada.json }}"
`;

// Os passos que produzem resultado e por isso nascem com `id`: sem ele, ninguém os lê depois.
// FLOW-04: os laços (`<id>.items`) e o `aggregate` também.
const COM_RESULTADO = [
  'http',
  'tool',
  'filter',
  'sort',
  'date',
  'code',
  'for_each',
  'while',
  'aggregate',
  // FLOW-05: os passos de arquivo que deixam o arquivo para os seguintes; o `artifact` entrega e
  // não tem resultado.
  'attachments',
  'image.normalize',
  'document.render',
];
// O nome do id que o passo novo ganha: o `type` com ponto não é id válido.
const BASE_DO_ID = {
  attachments: 'anexos',
  'image.normalize': 'imagem',
  'document.render': 'pdf',
};
const LISTAS_DE_PASSOS = ['then', 'else', 'default'];
// FLOW-04: o corpo do laço é o `steps` dele, e a rota de erro de qualquer passo, o `on_error.steps`.
const LACOS = ['for_each', 'while'];
const ROTA = 'on_error';
// FLOW-11 (S11, G16): as correntes soltas, `detached: [{steps: [...]}]` na raiz do flow.
const SOLTOS = 'detached';
export const ehCorrente = lista =>
  new RegExp(`^/${SOLTOS}/\\d+/steps$`).test(lista || '');
export const estaSolto = ponteiro =>
  String(ponteiro || '').startsWith(`/${SOLTOS}/`);

export const escapar = chave => chave.replace(/~/g, '~0').replace(/\//g, '~1');
const partes = ponteiro =>
  ponteiro
    .split('/')
    .slice(1)
    .map(p => p.replace(/~1/g, '/').replace(/~0/g, '~'));

const paiDe = ponteiro => ponteiro.slice(0, ponteiro.lastIndexOf('/'));
const ultimo = ponteiro => partes(ponteiro).pop();

// O `else` e o `default` podem faltar; as outras listas de passos (os `steps`, o `then`, um caso do
// `switch`, os passos de um ramo do Router) não, e vazias ou nulas o compilador as recusa. Por isso
// o último passo delas não sai pelo canvas: a pessoa troca por outro (insere e depois exclui).
// FLOW-04: a rota de erro também é opcional: sem o último passo dela, sai o `on_error` inteiro.
const ehRota = lista => {
  const [dono, campo] = partes(lista).slice(-2);
  return dono === ROTA && campo === 'steps';
};
export const opcional = lista => {
  const [dono, campo] = partes(lista).slice(-2);
  return (
    (['else', 'default'].includes(campo) && dono !== 'cases') ||
    ehRota(lista) ||
    ehCorrente(lista)
  );
};
export const podeExcluir = (lista, total) => total > 1 || opcional(lista);

// Texto sem quebra no fim junta a linha nova à última; com ela, todo bloco termina em `\n`.
const comFimDeLinha = texto =>
  texto && !texto.endsWith('\n') ? `${texto}\n` : texto;

const ler = texto => parseDocument(texto);

const parDe = (mapa, chave) =>
  mapa.items.find(par => isScalar(par.key) && String(par.key.value) === chave);

// O nó no ponteiro: mapa pela chave em texto (o caso `1:` de um switch é número no YAML), lista
// pelo índice.
function noEm(doc, ponteiro) {
  return partes(ponteiro).reduce((no, parte) => {
    if (isSeq(no)) return no.items[Number(parte)];
    if (isMap(no)) return parDe(no, parte)?.value;
    return undefined;
  }, doc.contents);
}

// As listas de passos dentro de um passo, na forma do `_sublistas` do compilador.
function sublistas(passo, ponteiro) {
  const listas = LISTAS_DE_PASSOS.filter(campo =>
    isSeq(passo.get(campo, true))
  ).map(campo => ({
    ponteiro: `${ponteiro}/${campo}`,
    no: passo.get(campo, true),
  }));
  const casos = passo.get('type') === 'switch' && passo.get('cases', true);
  if (isMap(casos)) {
    casos.items
      .filter(par => isSeq(par.value))
      .forEach(par =>
        listas.push({
          ponteiro: `${ponteiro}/cases/${escapar(String(par.key?.value))}`,
          no: par.value,
        })
      );
  }
  if (LACOS.includes(passo.get('type')) && isSeq(passo.get('steps', true))) {
    listas.push({
      ponteiro: `${ponteiro}/steps`,
      no: passo.get('steps', true),
    });
  }
  const quandoFalha = passo.get(ROTA, true);
  if (isMap(quandoFalha) && isSeq(quandoFalha.get('steps', true))) {
    listas.push({
      ponteiro: `${ponteiro}/${ROTA}/steps`,
      no: quandoFalha.get('steps', true),
    });
  }
  const ramos = passo.get('type') === 'branches' && passo.get('branches', true);
  if (isSeq(ramos)) {
    ramos.items.forEach((ramo, i) => {
      const passos = isMap(ramo) && ramo.get('steps', true);
      if (isSeq(passos)) {
        listas.push({
          ponteiro: `${ponteiro}/branches/${i}/steps`,
          no: passos,
        });
      }
    });
  }
  return listas;
}

// Cada passo do documento, em qualquer nível, com o ponteiro dele. FLOW-12 (G2): a ordem é a do
// documento, e as pegadas (`pegadasDoFlow.js`) leem o "acima de" por ela.
export function percorrer(doc, aoPasso) {
  const descer = (lista, ponteiro) =>
    lista.items.forEach((passo, i) => {
      if (!isMap(passo)) return;
      aoPasso(passo, `${ponteiro}/${i}`);
      sublistas(passo, `${ponteiro}/${i}`).forEach(sub =>
        descer(sub.no, sub.ponteiro)
      );
    });
  const passos = isMap(doc.contents) && doc.contents.get('steps', true);
  if (isSeq(passos)) descer(passos, '/steps');
  // FLOW-11 (S11): as correntes soltas (`detached`) também são passos do documento: o cursor as
  // acha, e os ids delas contam para o id repetido.
  const soltos = isMap(doc.contents) && doc.contents.get(SOLTOS, true);
  if (isSeq(soltos)) {
    soltos.items.forEach((corrente, k) => {
      const lista = isMap(corrente) && corrente.get('steps', true);
      if (isSeq(lista)) descer(lista, `/${SOLTOS}/${k}/steps`);
    });
  }
}

const inicioDaLinha = (texto, i) => texto.lastIndexOf('\n', i - 1) + 1;

// Onde um item de lista em bloco está no texto: `inicio` é a linha do `- `, `comeco` sobe pelos
// comentários colados acima dele (sem linha em branco no meio), e `fim` é logo depois da última
// linha dele.
//
// O comentário mais recuado que o traço é da lista de dentro do item de cima (um passo comentado no
// fim do `then`), não deste: `comeco` para nele. E o `yaml` estende o item que termina num
// comentário assim pelas linhas em branco até o recuo da linha do `- ` seguinte; `fim` volta para
// o fim da última linha com texto.
function limitesDoItem(texto, item) {
  const inicio = inicioDaLinha(texto, item.range[0]);
  const traco = texto.slice(inicio, item.range[0]).indexOf('-');
  let comeco = inicio;
  while (comeco > 0) {
    const anterior = inicioDaLinha(texto, comeco - 1);
    const recuo = texto.slice(anterior, comeco).match(/^[ \t]*#/);
    if (!recuo || recuo[0].length - 1 > traco) break;
    comeco = anterior;
  }
  let fim = item.range[1];
  while (fim > item.range[0] && /\s/.test(texto[fim - 1])) fim -= 1;
  fim = texto.indexOf('\n', fim) + 1 || texto.length;
  return { comeco, inicio, fim };
}

// A chave que abre a linha do `- ` (a primeira de um passo) não leva o traço junto.
const naLinhaDoTraco = (texto, par) =>
  !/^\s*$/.test(
    texto.slice(inicioDaLinha(texto, par.key.range[0]), par.key.range[0])
  );

// O par em bloco no texto: do começo da linha da chave (ou da chave, na linha do `- `) ao fim da
// última linha do valor.
function limitesDoPar(texto, par) {
  const inicio = naLinhaDoTraco(texto, par)
    ? par.key.range[0]
    : inicioDaLinha(texto, par.key.range[0]);
  let fim = par.value?.range ? par.value.range[1] : par.key.range[1];
  while (fim > par.key.range[1] && /\s/.test(texto[fim - 1])) fim -= 1;
  fim = texto.indexOf('\n', fim) + 1 || texto.length;
  return { inicio, fim };
}

// A lista é editável pelo texto quando é bloco, com cada item na própria linha depois do `- `.
const emBloco = (texto, lista) =>
  isSeq(lista) &&
  !lista.flow &&
  lista.items.length > 0 &&
  lista.items.every(item =>
    /^ *- +$/.test(
      texto.slice(inicioDaLinha(texto, item.range[0]), item.range[0])
    )
  );

const recuoDoTraco = (texto, item) => {
  const linha = texto.slice(inicioDaLinha(texto, item.range[0]), item.range[0]);
  return ' '.repeat(linha.indexOf('-'));
};

// O passo como item de lista, no recuo da lista.
function textoDoItem(modelo, recuo) {
  const linhas = stringify(modelo, { lineWidth: 0 }).trimEnd().split('\n');
  return `${linhas
    .map((linha, i) => (i === 0 ? `${recuo}- ${linha}` : `${recuo}  ${linha}`))
    .join('\n')}\n`;
}

// A lista separa os passos com linha em branco (o estilo do exemplo da SPEC)? O passo novo segue.
const separaComLinhaEmBranco = (texto, lista) =>
  lista.items.some(
    (item, i) =>
      i > 0 &&
      /^[ \t]*\n/.test(
        texto.slice(
          limitesDoItem(texto, lista.items[i - 1]).fim,
          limitesDoItem(texto, item).comeco
        )
      )
  );

const idsDoDocumento = doc => {
  const ids = new Set();
  percorrer(doc, passo => {
    const id = passo.get('id');
    if (typeof id === 'string') ids.add(id);
  });
  return ids;
};

const idLivre = (ids, base) => {
  let n = 1;
  while (ids.has(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
};

// FLOW-07: o JavaScript do passo `code` vai em bloco (`code: |-`), uma linha do código por linha
// do YAML, que é como se lê e se compara num diff. O indicador (`|-`, `|2-` com recuo na primeira
// linha) é o do `yaml`, que devolve o texto igual. O branco do fim fica de fora: num bloco `|+` a
// linha em branco que separa os passos viraria parte do código, e no JS ele não muda nada. Por isso
// o editor de JS compara o que tem com o que volta sem o branco do fim (`mesmoCodigo`).
export const codigoEscrito = valor => valor.replace(/\s+$/, '');
export const mesmoCodigo = (a, b) => codigoEscrito(a) === codigoEscrito(b);
const literal = valor => {
  const no = new Scalar(valor);
  no.type = Scalar.BLOCK_LITERAL;
  return no;
};
// FLOW-05: o modelo HTML do `document.render` também, uma linha do HTML por linha do YAML.
const EM_BLOCO = { code: 'code', template: 'document.render' };
const vaiEmBloco = (mapa, chave, valor) =>
  typeof valor === 'string' &&
  !!EM_BLOCO[chave] &&
  mapa.get('type') === EM_BLOCO[chave];

// O passo novo com os campos obrigatórios do schema (`flow.schema.json`, `required`) e valores que
// compilam onde o passo cabe; quem insere ajusta depois. O `validate` confere o primeiro parâmetro
// do flow, que é o caso de quase todo flow (o CPF, o e-mail).
function modeloDoPasso(tipo, doc) {
  const raiz = isMap(doc.contents) ? doc.contents : null;
  const params = raiz?.get('params', true);
  const primeiro = isMap(params) && params.items[0]?.key?.value;
  const valor = primeiro ? `{{ params.${primeiro} }}` : "{{ '' }}";
  const MODELOS = {
    http: { type: 'http', method: 'GET', url: 'https://api.exemplo.com' },
    tool: { type: 'tool', name: 'nome_da_tool' },
    if: {
      type: 'if',
      when: primeiro ? `{{ params.${primeiro} != '' }}` : '{{ true }}',
      then: [{ type: 'set', vars: { caminho: 'verdadeiro' } }],
    },
    switch: {
      type: 'switch',
      value: valor,
      cases: { a: [{ type: 'set', vars: { caso: 'a' } }] },
    },
    branches: {
      type: 'branches',
      mode: 'sequential',
      branches: [
        { name: 'ramo_1', steps: [{ type: 'set', vars: { ramo_1: true } }] },
        { name: 'ramo_2', steps: [{ type: 'set', vars: { ramo_2: true } }] },
      ],
    },
    set: { type: 'set', vars: { nome: 'valor' } },
    validate: {
      type: 'validate',
      value: valor,
      rule: 'required',
      message: 'Campo obrigatório.',
    },
    filter: {
      type: 'filter',
      items: '{{ [] }}',
      where: '{{ item != null }}',
    },
    sort: { type: 'sort', items: '{{ [] }}', by: [{ field: '{{ item }}' }] },
    // FLOW-04: o laço nasce com um passo no corpo, porque corpo vazio o compilador recusa; quem
    // insere troca a lista e o passo depois.
    for_each: {
      type: 'for_each',
      items: '{{ [] }}',
      steps: [{ type: 'set', vars: { item: '{{ iteration.item }}' } }],
    },
    while: {
      type: 'while',
      max: 3,
      steps: [{ type: 'set', vars: { volta: '{{ iteration.index }}' } }],
    },
    aggregate: {
      type: 'aggregate',
      items: '{{ [] }}',
      compute: { total: { op: 'count' } },
    },
    date: { type: 'date', op: 'now' },
    respond: { type: 'respond', value: { recebido: true } },
    return: { type: 'return', value: { ok: true } },
    fail: { type: 'fail', message: 'Explique por que o flow parou.' },
    code: {
      type: 'code',
      code: literal(
        '// Lê params, ctx, vars, input e os passos anteriores pelo id.\nreturn { ok: true };'
      ),
    },
    // FLOW-05: o `file` nasce como a fórmula vazia, que compila; quem insere aponta para o arquivo
    // de um passo anterior. O modelo do PDF lê só o `data`, e nasce lendo o `titulo` dele.
    attachments: { type: 'attachments' },
    'image.normalize': { type: 'image.normalize', file: "{{ '' }}" },
    'document.render': {
      type: 'document.render',
      template: literal('<h1>{{ titulo }}</h1>'),
      data: { titulo: 'Título do documento' },
      name: 'documento.pdf',
    },
    artifact: { type: 'artifact', file: "{{ '' }}" },
  };
  const modelo = MODELOS[tipo];
  if (!COM_RESULTADO.includes(tipo)) return modelo;
  const base = BASE_DO_ID[tipo] || tipo;
  return { id: idLivre(idsDoDocumento(doc), base), ...modelo };
}

// Junta o texto sem deixar duas linhas em branco onde o passo saiu.
function juntar(antes, depois) {
  if (/\n[ \t]*\n$/.test(antes) && /^[ \t]*\n/.test(depois)) {
    return antes + depois.replace(/^[ \t]*\n/, '');
  }
  if (!depois.trim()) return antes.replace(/\n(?:[ \t]*\n)+$/, '\n');
  return antes + depois;
}

// FLOW-04: a rota de erro que ainda não existe nasce inteira (`on_error:` com o `steps:` e o passo),
// sempre em bloco, no lugar do `on_error: continue` (ou `fail`) que houver, ou no fim do passo.
function novaRota(base, doc, lista, modelo) {
  const passo = noEm(doc, paiDe(paiDe(lista)));
  if (!isMap(passo)) return null;
  const par = parDe(passo, ROTA);
  if (passo.flow || !passo.items.length) {
    passo.set(ROTA, { steps: [modelo] });
    return { texto: doc.toString(), ponteiro: `${lista}/0` };
  }
  const irmao = par || passo.items[passo.items.length - 1];
  const linhaDoIrmao = base.slice(
    inicioDaLinha(base, irmao.key.range[0]),
    irmao.key.range[0]
  );
  const recuo = ' '.repeat(
    naLinhaDoTraco(base, irmao)
      ? linhaDoIrmao.indexOf('-') + 2
      : linhaDoIrmao.length
  );
  const bloco = `${recuo}${ROTA}:\n${recuo}  steps:\n${textoDoItem(modelo, `${recuo}    `)}`;
  if (par) {
    const { inicio, fim } = limitesDoPar(base, par);
    const escrito = naLinhaDoTraco(base, par)
      ? bloco.slice(recuo.length)
      : bloco;
    return {
      texto: base.slice(0, inicio) + escrito + base.slice(fim),
      ponteiro: `${lista}/0`,
    };
  }
  const { fim } = limitesDoPar(base, irmao);
  return {
    texto: base.slice(0, fim) + bloco + base.slice(fim),
    ponteiro: `${lista}/0`,
  };
}

// Inserir `tipo` na posição `indice` da lista `lista` (um ponteiro, `/steps/1/then`). A lista que
// ainda não existe (o `else` de um `if`, o `default` de um `switch`) nasce no passo dono dela.
export function inserirPasso(texto, { lista, indice }, tipo) {
  const base = comFimDeLinha(texto);
  const doc = ler(base);
  const modelo = modeloDoPasso(tipo, doc);
  const ponteiro = `${lista}/${indice}`;
  const seq = noEm(doc, lista);

  if (ehRota(lista) && !isMap(noEm(doc, paiDe(lista)))) {
    const rota = novaRota(base, doc, lista, modelo);
    if (rota) return rota;
  }

  if (emBloco(base, seq)) {
    const recuo = recuoDoTraco(base, seq.items[0]);
    const item = textoDoItem(modelo, recuo);
    const branco = separaComLinhaEmBranco(base, seq) ? '\n' : '';
    if (indice < seq.items.length) {
      const { comeco } = limitesDoItem(base, seq.items[indice]);
      return {
        texto: base.slice(0, comeco) + item + branco + base.slice(comeco),
        ponteiro,
      };
    }
    const { fim } = limitesDoItem(base, seq.items[seq.items.length - 1]);
    return {
      texto: base.slice(0, fim) + branco + item + base.slice(fim),
      ponteiro: `${lista}/${seq.items.length}`,
    };
  }

  const dono = noEm(doc, paiDe(lista));
  const campo = ultimo(lista);
  if (seq === undefined && isMap(dono) && !dono.flow && dono.items.length) {
    const recuo = ' '.repeat(
      dono.range[0] - inicioDaLinha(base, dono.range[0])
    );
    const fim = dono.range[1];
    const bloco = `${recuo}${campo}:\n${textoDoItem(modelo, `${recuo}  `)}`;
    return {
      texto: base.slice(0, fim) + bloco + base.slice(fim),
      ponteiro: `${lista}/0`,
    };
  }

  // A lista nula (`then:` sem item, que fica quando a pessoa apaga os passos no código): o item
  // entra na linha de baixo da chave, e o `~` ou `null` escrito sai; o comentário da linha fica.
  const par = isMap(dono) && !dono.flow && parDe(dono, campo);
  const fimDaChave = par && base.indexOf('\n', par.key.range[1]);
  if (
    par &&
    isScalar(par.value) &&
    par.value.value === null &&
    par.value.range[1] <= fimDaChave
  ) {
    const [de, ate] = par.value.range;
    const recuo = ' '.repeat(
      par.key.range[0] - inicioDaLinha(base, par.key.range[0])
    );
    const antes =
      ate > de ? base.slice(0, de).replace(/[ \t]+$/, '') : base.slice(0, de);
    return {
      texto:
        antes +
        base.slice(ate, fimDaChave + 1) +
        textoDoItem(modelo, `${recuo}  `) +
        base.slice(fimDaChave + 1),
      ponteiro: `${lista}/0`,
    };
  }

  // Lista em estilo fluxo, ou vazia: pela Document API, com o texto todo reescrito.
  if (isSeq(seq)) {
    seq.items.splice(indice, 0, doc.createNode(modelo));
    return {
      texto: doc.toString(),
      ponteiro: `${lista}/${Math.min(indice, seq.items.length - 1)}`,
    };
  }
  dono.set(campo, doc.createNode([modelo]));
  return { texto: doc.toString(), ponteiro: `${lista}/0` };
}

// Tira a corrente `k` do `detached`; sem outra corrente, o `detached` sai inteiro.
function tirarCorrente(base, doc, k) {
  const soltos = doc.contents.get(SOLTOS, true);
  if (soltos.items.length === 1) {
    const par = parDe(doc.contents, SOLTOS);
    if (!doc.contents.flow) {
      const { fim } = limitesDoPar(base, par);
      let inicio = inicioDaLinha(base, par.key.range[0]);
      while (inicio > 0) {
        const anterior = inicioDaLinha(base, inicio - 1);
        if (!/^[ \t]*#/.test(base.slice(anterior, inicio))) break;
        inicio = anterior;
      }
      return juntar(base.slice(0, inicio), base.slice(fim)).replace(
        /\n(?:[ \t]*\n)+$/,
        '\n'
      );
    }
    doc.contents.delete(SOLTOS);
    return doc.toString();
  }
  if (emBloco(base, soltos)) {
    const { comeco, fim } = limitesDoItem(base, soltos.items[k]);
    return juntar(base.slice(0, comeco), base.slice(fim));
  }
  soltos.items.splice(k, 1);
  return doc.toString();
}

// Excluir o passo no ponteiro. O comentário colado acima dele fica (passa a ser do seguinte). O
// `else` e o `default` que ficariam vazios saem inteiros: lista vazia é erro no compilador, e sem
// eles o `if` e o `switch` seguem adiante, que é o que a pessoa quis ao tirar o último passo. O
// último passo de uma lista obrigatória fica (`podeExcluir`), e o texto volta como estava.
export function excluirPasso(texto, ponteiro) {
  const base = comFimDeLinha(texto);
  const doc = ler(base);
  const lista = paiDe(ponteiro);
  const seq = noEm(doc, lista);
  const indice = Number(ultimo(ponteiro));

  if (isSeq(seq) && !podeExcluir(lista, seq.items.length)) {
    return { texto, ponteiro };
  }

  // FLOW-11: o último passo de uma corrente solta leva a corrente, e a última leva o `detached`.
  if (isSeq(seq) && seq.items.length === 1 && ehCorrente(lista)) {
    return {
      texto: tirarCorrente(base, doc, Number(partes(lista)[1])),
      ponteiro: null,
    };
  }

  if (emBloco(base, seq)) {
    const { comeco, inicio, fim } = limitesDoItem(base, seq.items[indice]);
    const dono = noEm(doc, paiDe(lista));
    const campo = ultimo(lista);
    // FLOW-04: o último passo da rota de erro leva o `on_error` inteiro, que vazio não compila.
    if (seq.items.length === 1 && ehRota(lista)) {
      const passo = noEm(doc, paiDe(paiDe(lista)));
      const par = isMap(passo) && !passo.flow && parDe(passo, ROTA);
      if (par) {
        const { inicio: de, fim: ate } = limitesDoPar(base, par);
        return {
          texto: juntar(base.slice(0, de), base.slice(ate)),
          ponteiro: null,
        };
      }
    }
    if (seq.items.length === 1 && isMap(dono) && !dono.flow) {
      const chave = parDe(dono, campo).key;
      const desde = inicioDaLinha(base, chave.range[0]);
      return {
        texto: juntar(base.slice(0, desde), base.slice(fim)),
        ponteiro: null,
      };
    }
    // O comentário que fica cola no passo seguinte, sem a linha em branco que separava o excluído.
    const depois =
      comeco < inicio
        ? base.slice(fim).replace(/^(?:[ \t]*\n)+/, '')
        : base.slice(fim);
    return { texto: juntar(base.slice(0, inicio), depois), ponteiro: null };
  }

  seq.items.splice(indice, 1);
  return { texto: doc.toString(), ponteiro: null };
}

// Trocar o item `indice` com o vizinho `indice + delta` na lista: os blocos de texto trocam de
// lugar, cada um com os comentários de cima, e o que separa os dois fica onde estava.
function trocarItens(texto, lista, indice, delta) {
  const base = comFimDeLinha(texto);
  const doc = ler(base);
  const seq = noEm(doc, lista);
  const outro = indice + delta;
  if (!isSeq(seq) || outro < 0 || outro >= seq.items.length) return null;
  const [i, j] = [Math.min(indice, outro), Math.max(indice, outro)];

  if (!emBloco(base, seq)) {
    const [item] = seq.items.splice(indice, 1);
    seq.items.splice(outro, 0, item);
    return doc.toString();
  }
  const a = limitesDoItem(base, seq.items[i]);
  const b = limitesDoItem(base, seq.items[j]);
  return (
    base.slice(0, a.comeco) +
    base.slice(b.comeco, b.fim) +
    base.slice(a.fim, b.comeco) +
    base.slice(a.comeco, a.fim) +
    base.slice(b.fim)
  );
}

// O ramo `de` do Router vai para a posição `para`: a ordem dos ramos é a ordem em que rodam no
// `sequential` e em que o `match: first` os confere (D16), então é a ordem do YAML que muda.
export function reordenarRamo(texto, ponteiro, de, para) {
  const lista = `${ponteiro}/branches`;
  const passo = de < para ? 1 : -1;
  let atual = texto;
  for (let i = de; i !== para; i += passo) {
    atual = trocarItens(atual, lista, i, passo) ?? atual;
  }
  return { texto: atual, ponteiro };
}

// Trocar um campo escalar do passo (o `mode` e o `on_error` do Router, pelo menu do nó). O valor
// é trocado no lugar, e o comentário da linha fica; o campo que não existe entra logo abaixo do
// `type`.
export function definirCampo(texto, ponteiro, campo, valor) {
  const base = comFimDeLinha(texto);
  const doc = ler(base);
  const passo = noEm(doc, ponteiro);
  const par = parDe(passo, campo);
  const escrito = stringify(valor).trimEnd();

  if (!passo.flow && par && isScalar(par.value)) {
    const [inicio, fim] = par.value.range;
    return {
      texto: base.slice(0, inicio) + escrito + base.slice(fim),
      ponteiro,
    };
  }
  const tipo = parDe(passo, 'type');
  if (!passo.flow && !par && tipo) {
    const recuo = ' '.repeat(
      tipo.key.range[0] - inicioDaLinha(base, tipo.key.range[0])
    );
    const fimDoTipo = base.indexOf('\n', tipo.value.range[1]) + 1;
    return {
      texto: `${base.slice(0, fimDoTipo)}${recuo}${campo}: ${escrito}\n${base.slice(fimDoTipo)}`,
      ponteiro,
    };
  }
  passo.set(campo, valor);
  return { texto: doc.toString(), ponteiro };
}

// As linhas (base 1) do passo no texto, do `- ` à última linha dele: é o que o código destaca
// quando o passo é escolhido no canvas.
export function linhasDoPasso(texto, ponteiro) {
  const { doc, contador } = documentoLido(texto);
  const no = noEm(doc, ponteiro);
  if (!no?.range) return null;
  const inicio = contador.linePos(inicioDaLinha(texto, no.range[0])).line;
  const fim = contador.linePos(Math.max(no.range[1] - 1, no.range[0])).line;
  return { inicio, fim };
}

// FLOW-07: a linha (base 1) do YAML em que o código do passo `code` começa. No bloco (`code: |-`) é a
// seguinte à do `|`, e a linha N do código é a `inicio + N - 1` do YAML; noutra forma (entre aspas,
// numa linha só) o código inteiro mora na linha do valor (`bloco: false`). É o caminho entre o erro
// do compilador, que fala na linha do YAML, e o editor de JS, que fala na do código.
// FLOW-05: o modelo do `document.render` (`campo: 'template'`) segue o mesmo caminho.
export function linhaDoCodigo(texto, ponteiro, campo = 'code') {
  const { doc, contador } = documentoLido(texto);
  const no = noEm(doc, `${ponteiro}/${campo}`);
  if (!isScalar(no) || !no.range) return null;
  const linha = contador.linePos(no.range[0]).line;
  const bloco = [Scalar.BLOCK_LITERAL, Scalar.BLOCK_FOLDED].includes(no.type);
  return { inicio: bloco ? linha + 1 : linha, bloco };
}

// Os `id` dos passos escritos antes do passo no ponteiro, na ordem do documento: os que o código de
// um passo `code` pode ler (G1). Um passo de outro ramo também entra; quem decide o escopo exato é o
// compilador, e aqui é só a sugestão do editor.
export function idsAntesDe(texto, ponteiro) {
  const ids = [];
  let chegou = false;
  percorrer(documentoLido(texto).doc, (passo, onde) => {
    if (onde === ponteiro) chegou = true;
    const id = passo.get('id');
    if (!chegou && typeof id === 'string' && ID_VALIDO.test(id)) ids.push(id);
  });
  return ids;
}

// O passo mais fundo que contém a posição do cursor, para o canvas seguir o código. A linha do
// `- ` conta como do passo.
export function passoNoOffset(texto, offset) {
  let achado = null;
  percorrer(documentoLido(texto).doc, (passo, ponteiro) => {
    if (!passo.range) return;
    const inicio = inicioDaLinha(texto, passo.range[0]);
    if (offset >= inicio && offset <= passo.range[1]) achado = ponteiro;
  });
  return achado;
}

// -- a caixa do passo (task 5.4c, D20): o formulário e o YAML do passo editam o MESMO texto, no
// mesmo estilo cirúrgico de cima. Um valor trocado é trocado no lugar (o comentário da linha fica);
// um campo novo entra no fim do mapa dele, no recuo dos irmãos; o mapa em fluxo (`query: { … }`)
// continua em fluxo, numa linha. Só o que a pessoa mexeu muda de texto.

const ehEscalar = valor => valor === null || typeof valor !== 'object';
const ehColecao = no => isMap(no) || isSeq(no);

// Um escalar numa linha. O texto com quebra vai entre aspas duplas (JSON também é YAML), para
// nunca virar um bloco `|` no meio de um mapa.
const escalar = valor =>
  typeof valor === 'string' && valor.includes('\n')
    ? JSON.stringify(valor)
    : stringify(valor, { lineWidth: 0 }).trimEnd();

// Em fluxo, a vírgula e as chaves encerram o escalar sem aspas: o texto que as tem vai entre aspas.
const escalarEmFluxo = valor =>
  typeof valor === 'string' && /[,[\]{}#&*!|>'"%@`]|: |^\s|\s$|^$/.test(valor)
    ? JSON.stringify(valor)
    : escalar(valor);

const emFluxo = valor => {
  if (Array.isArray(valor)) return `[${valor.map(emFluxo).join(', ')}]`;
  if (ehEscalar(valor)) return escalarEmFluxo(valor);
  const pares = Object.entries(valor).map(
    ([chave, v]) => `${escalarEmFluxo(chave)}: ${emFluxo(v)}`
  );
  return pares.length ? `{ ${pares.join(', ')} }` : '{}';
};

const filhoDe = (no, chave) => {
  if (isSeq(no)) return no.items[Number(chave)];
  if (isMap(no)) return parDe(no, String(chave))?.value;
  return undefined;
};

const raizDe = (doc, ponteiro) =>
  ponteiro ? noEm(doc, ponteiro) : doc.contents;

// `chave: valor` em bloco, terminado em `\n`. A coleção vai em fluxo quando os irmãos já escrevem
// as deles assim (o `params` do exemplo da SPEC).
function textoDoPar(chave, valor, recuo, fluxo) {
  if (ehEscalar(valor) || fluxo) {
    const escrito = fluxo ? emFluxo(valor) : escalar(valor);
    return `${recuo}${escalar(chave)}: ${escrito}\n`;
  }
  const escrito = stringify({ [chave]: valor }, { lineWidth: 0 });
  // O bloco do `code` guarda as linhas em branco do fim (`|+`): só a quebra final sai, e a linha
  // em branco fica sem o recuo.
  if (isScalar(valor)) {
    const linhas = escrito.replace(/\n$/, '').split('\n');
    return `${linhas.map(linha => (linha ? recuo + linha : '')).join('\n')}\n`;
  }
  const linhas = escrito.trimEnd().split('\n');
  return `${linhas.map(linha => recuo + linha).join('\n')}\n`;
}

function escreverEmFluxo(base, mapa, par, chave, valor) {
  const i = mapa.items.indexOf(par);
  const fimDe = p => (p.value?.range ? p.value.range[1] : p.key.range[1]);
  if (valor === undefined) {
    if (!par) return base;
    if (i < mapa.items.length - 1) {
      return (
        base.slice(0, par.key.range[0]) +
        base.slice(mapa.items[i + 1].key.range[0])
      );
    }
    if (i > 0)
      return base.slice(0, fimDe(mapa.items[i - 1])) + base.slice(fimDe(par));
    return `${base.slice(0, mapa.range[0])}{}${base.slice(mapa.range[1])}`;
  }
  if (par?.value?.range && par.value.range[1] > par.value.range[0]) {
    const [de, ate] = par.value.range;
    return base.slice(0, de) + emFluxo(valor) + base.slice(ate);
  }
  const novo = `${escalarEmFluxo(chave)}: ${emFluxo(valor)}`;
  const derradeiro = mapa.items[mapa.items.length - 1];
  if (!derradeiro)
    return `${base.slice(0, mapa.range[0])}{ ${novo} }${base.slice(mapa.range[1])}`;
  const depois = fimDe(derradeiro);
  return `${base.slice(0, depois)}, ${novo}${base.slice(depois)}`;
}

function escreverPar(base, doc, mapa, chave, valor) {
  const par = parDe(mapa, chave);
  if (mapa.flow) return escreverEmFluxo(base, mapa, par, chave, valor);
  if (valor === undefined) {
    if (!par) return base;
    const i = mapa.items.indexOf(par);
    const proximo = mapa.items[i + 1];
    if (naLinhaDoTraco(base, par) && proximo) {
      return base.slice(0, par.key.range[0]) + base.slice(proximo.key.range[0]);
    }
    const { inicio, fim } = limitesDoPar(base, par);
    return base.slice(0, inicio) + base.slice(fim);
  }
  const [de, ate] = par?.value?.range || [];
  const bloco = vaiEmBloco(mapa, chave, valor);
  if (par && isScalar(par.value) && ehEscalar(valor) && ate > de && !bloco) {
    return base.slice(0, de) + escalar(valor) + base.slice(ate);
  }
  if (par && ehColecao(par.value) && par.value.flow && !ehEscalar(valor)) {
    return base.slice(0, de) + emFluxo(valor) + base.slice(ate);
  }
  const irmao = par || mapa.items[0];
  if (!irmao) {
    mapa.set(chave, valor);
    return doc.toString();
  }
  const recuo = ' '.repeat(
    irmao.key.range[0] - inicioDaLinha(base, irmao.key.range[0])
  );
  const colecoes = mapa.items.filter(p => ehColecao(p.value));
  const fluxo =
    !ehEscalar(valor) &&
    colecoes.length > 0 &&
    colecoes.every(p => p.value.flow);
  const novo = textoDoPar(
    chave,
    bloco ? literal(codigoEscrito(valor)) : valor,
    recuo,
    fluxo
  );
  if (!par) {
    const { fim } = limitesDoPar(base, mapa.items[mapa.items.length - 1]);
    return base.slice(0, fim) + novo + base.slice(fim);
  }
  const { inicio, fim } = limitesDoPar(base, par);
  const escrito = naLinhaDoTraco(base, par) ? novo.slice(recuo.length) : novo;
  return base.slice(0, inicio) + escrito + base.slice(fim);
}

// Escrever `valor` no campo `caminho` (`['body', 'cpf']`, `['branches', 0, 'when']`) do passo no
// ponteiro (ou do topo do flow, com o ponteiro vazio). `undefined` tira o campo, e o mapa que ficaria
// vazio sai junto (o `headers:` sem cabeçalho nenhum, que o compilador recusaria); o que falta no
// caminho nasce como objeto.
export function definirValor(texto, ponteiro, caminho, valor) {
  const mesmo = { texto, ponteiro };
  const base = comFimDeLinha(texto);
  const doc = ler(base);
  let mapa = raizDe(doc, ponteiro);
  let i = 0;
  while (i < caminho.length - 1) {
    const filho = filhoDe(mapa, caminho[i]);
    if (!ehColecao(filho)) break;
    mapa = filho;
    i += 1;
  }
  if (!isMap(mapa)) return mesmo;
  if (i < caminho.length - 1) {
    if (valor === undefined) return mesmo;
    const aninhado = caminho
      .slice(i + 1)
      .reduceRight((v, chave) => ({ [chave]: v }), valor);
    return {
      texto: escreverPar(base, doc, mapa, String(caminho[i]), aninhado),
      ponteiro,
    };
  }
  const chave = String(caminho[caminho.length - 1]);
  if (
    valor === undefined &&
    i > 0 &&
    mapa.items.length === 1 &&
    parDe(mapa, chave)
  ) {
    return definirValor(texto, ponteiro, caminho.slice(0, -1), undefined);
  }
  return { texto: escreverPar(base, doc, mapa, chave, valor), ponteiro };
}

// Trocar o nome de uma chave (um campo do corpo, um parâmetro do flow) sem tocar no valor dela.
// O nome que já existe no mapa não entra: as duas chaves virariam uma.
export function renomearChave(texto, ponteiro, caminho, nova) {
  const base = comFimDeLinha(texto);
  const doc = ler(base);
  const mapa = caminho
    .slice(0, -1)
    .reduce((no, chave) => filhoDe(no, chave), raizDe(doc, ponteiro));
  const par = isMap(mapa) && parDe(mapa, String(caminho[caminho.length - 1]));
  if (!par || !nova || parDe(mapa, nova)) return { texto, ponteiro };
  const [de, ate] = par.key.range;
  const escrito = mapa.flow ? escalarEmFluxo(nova) : escalar(nova);
  return { texto: base.slice(0, de) + escrito + base.slice(ate), ponteiro };
}

// O valor no ponteiro, como objeto (o passo inteiro, o topo do flow com o ponteiro vazio).
export function valorEm(texto, ponteiro) {
  const no = raizDe(documentoLido(texto).doc, ponteiro);
  return no && typeof no.toJSON === 'function' ? no.toJSON() : (no ?? null);
}

// O passo sozinho, como a caixa o mostra no YAML dela: sem o `- ` e sem o recuo da lista. Volta
// também a linha (base 1) em que ele começa, para os erros do compilador caírem na linha certa.
export function trechoDoPasso(texto, ponteiro) {
  const base = comFimDeLinha(texto);
  const contador = new LineCounter();
  const doc = parseDocument(base, { lineCounter: contador });
  const item = noEm(doc, ponteiro);
  const seq = noEm(doc, paiDe(ponteiro));
  if (!isMap(item)) return null;
  if (!emBloco(base, seq)) {
    return { texto: stringify(item.toJSON(), { lineWidth: 0 }), linha: null };
  }
  const { inicio, fim } = limitesDoItem(base, item);
  const coluna = item.range[0] - inicio;
  const trecho = base
    .slice(inicio, fim)
    .split('\n')
    .map((linha, i) => {
      if (i === 0) return linha.slice(coluna);
      const recuo = linha.match(/^ */)[0].length;
      return linha.slice(Math.min(coluna, recuo));
    })
    .join('\n');
  return { texto: trecho, linha: contador.linePos(inicio).line };
}

// O YAML do passo editado na caixa volta ao lugar dele, no recuo da lista. O que não é um mapa com
// `type` (a pessoa no meio da digitação) não entra: o flow fica como estava até o trecho valer.
export function trocarPasso(texto, ponteiro, trecho) {
  const novo = parseDocument(trecho);
  if (
    novo.errors.length ||
    !isMap(novo.contents) ||
    !novo.contents.get('type')
  ) {
    return null;
  }
  const base = comFimDeLinha(texto);
  const doc = ler(base);
  const item = noEm(doc, ponteiro);
  const seq = noEm(doc, paiDe(ponteiro));
  if (!isMap(item)) return null;
  if (!emBloco(base, seq)) {
    seq.items[Number(ultimo(ponteiro))] = doc.createNode(novo.toJSON());
    return { texto: doc.toString(), ponteiro };
  }
  const { inicio, fim } = limitesDoItem(base, item);
  const prefixo = base.slice(inicio, item.range[0]);
  const recuo = ' '.repeat(prefixo.length);
  const linhas = comFimDeLinha(trecho.trimEnd()).slice(0, -1).split('\n');
  const reescrito = linhas
    .map((linha, i) => {
      if (i === 0) return prefixo + linha;
      return linha ? recuo + linha : linha;
    })
    .join('\n');
  return {
    texto: `${base.slice(0, inicio)}${reescrito}\n${base.slice(fim)}`,
    ponteiro,
  };
}

// ---------------------------------------------------------------------------------------------
// FLOW-11 (task 5.2, S11, G17) — os gestos do canvas no padrão do n8n: ligar, apagar e religar
// conexões, renomear, substituir, copiar, colar, duplicar e desativar. Cada um é uma função pura que
// devolve `{texto, ponteiro}` (ou `{recusa}`, com o código do texto da SPEC), e a página o trata como
// uma edição só: um passo do desfazer (`CodigoYaml.vue`, `pushEditOperations`).
//
// O trabalho é feito sobre "blocos": o texto de um item de lista sem o recuo dela, do `- ` à última
// linha, com os comentários colados acima. Mover é tirar os blocos de uma lista e pô-los em outra no
// recuo dela: o passo leva o que tem dentro e os comentários (`commentBefore` e `comment`). A lista
// em estilo fluxo (`then: [ ... ]`) cai na Document API, como nas edições de antes.

const RESERVADOS = new Set([
  'params',
  'ctx',
  'vars',
  'item',
  'iteration',
  'error',
  'now',
]);

// Os tipos de passo do schema (`$defs/passo`, `type.enum`): o colar recusa o que não é um deles.
export const TIPOS_DE_PASSO = [
  'http',
  'tool',
  'if',
  'switch',
  'branches',
  'for_each',
  'while',
  'set',
  'validate',
  'filter',
  'sort',
  'aggregate',
  'date',
  'code',
  'attachments',
  'image.normalize',
  'document.render',
  'artifact',
  'return',
  'respond',
  'fail',
];

// Os campos que o Substituir guarda quando o tipo novo também os aceita (S11). O `on_error` vale em
// todo tipo (o schema o tem em todos); o `retry` do `code` é o da S6.
export const CAMPOS_GUARDADOS = {
  retry: ['http', 'tool', 'code'],
  timeout: ['http', 'code', 'image.normalize', 'document.render'],
};

const indentar = (bloco, recuo) =>
  bloco
    .split('\n')
    .map(linha => (linha ? recuo + linha : linha))
    .join('\n');

// O item `i` da lista como bloco, sem o recuo dela.
function blocoDoItem(base, seq, i) {
  const item = seq.items[i];
  if (!emBloco(base, seq)) return textoDoItem(item.toJSON(), '');
  const { comeco, fim } = limitesDoItem(base, item);
  const recuo = recuoDoTraco(base, item).length;
  return base
    .slice(comeco, fim)
    .split('\n')
    .map(linha => linha.slice(Math.min(recuo, linha.match(/^ */)[0].length)))
    .join('\n');
}

function blocosDaLista(base, doc, lista, de, ate) {
  const seq = noEm(doc, lista);
  if (!isSeq(seq)) return [];
  const blocos = [];
  for (let i = de; i < Math.min(ate, seq.items.length); i += 1) {
    blocos.push(comFimDeLinha(blocoDoItem(base, seq, i)));
  }
  return blocos;
}

// Os itens [de, ate) como UM bloco, com o que separa um do outro (a linha em branco) como estava:
// é o que o mover e o soltar levam de uma lista para outra.
function trechoDaLista(base, doc, lista, de, ate) {
  const seq = noEm(doc, lista);
  if (!isSeq(seq) || de >= ate) return [];
  if (!emBloco(base, seq)) return blocosDaLista(base, doc, lista, de, ate);
  const recuo = recuoDoTraco(base, seq.items[de]).length;
  const { comeco } = limitesDoItem(base, seq.items[de]);
  const { fim } = limitesDoItem(
    base,
    seq.items[Math.min(ate, seq.items.length) - 1]
  );
  return [
    comFimDeLinha(
      base
        .slice(comeco, fim)
        .split('\n')
        .map(linha =>
          linha.slice(Math.min(recuo, linha.match(/^ */)[0].length))
        )
        .join('\n')
    ),
  ];
}

// Os blocos como valores (para a Document API, nas listas em estilo fluxo).
const valoresDosBlocos = blocos => {
  const lido = parseDocument(blocos.join(''));
  return isSeq(lido.contents) ? lido.contents.toJSON() : [];
};

// Tira os itens [de, ate) da lista e devolve o texto e o que sumiu com eles:
// - `corrente`: era a corrente solta inteira, e ela saiu (com o `detached`, se era a última);
// - `lista`: era a lista opcional inteira (o `else`, o `default`, a rota de erro), e a chave saiu;
// - `nula`: era a lista obrigatória inteira, que fica nula (`then:`) para o religar pôr o passo nela;
// - `nada`: sobraram itens na lista.
function tirarItens(base, lista, de, ate) {
  const doc = ler(base);
  const seq = noEm(doc, lista);
  const todos = de === 0 && ate >= seq.items.length;
  if (todos && ehCorrente(lista)) {
    return {
      texto: tirarCorrente(base, doc, Number(partes(lista)[1])),
      sumiu: 'corrente',
    };
  }
  if (todos && opcional(lista)) {
    const dono = ehRota(lista)
      ? noEm(doc, paiDe(paiDe(lista)))
      : noEm(doc, paiDe(lista));
    const chave = ehRota(lista) ? ROTA : ultimo(lista);
    if (isMap(dono) && !dono.flow) {
      const { inicio, fim } = limitesDoPar(base, parDe(dono, chave));
      return {
        texto: juntar(base.slice(0, inicio), base.slice(fim)),
        sumiu: 'lista',
      };
    }
    dono.delete(chave);
    return { texto: doc.toString(), sumiu: 'lista' };
  }
  if (emBloco(base, seq)) {
    const { comeco } = limitesDoItem(base, seq.items[de]);
    const { fim } = limitesDoItem(base, seq.items[ate - 1]);
    const antes = base.slice(0, comeco);
    let depois = base.slice(fim);
    // Saiu o primeiro de uma lista separada por linha em branco: a do seguinte vai junto.
    if (ate < seq.items.length && !/\n[ \t]*\n$/.test(antes)) {
      depois = depois.replace(/^(?:[ \t]*\n)+/, '');
    }
    return { texto: juntar(antes, depois), sumiu: todos ? 'nula' : 'nada' };
  }
  seq.items.splice(de, ate - de);
  if (todos) {
    const dono = noEm(doc, paiDe(lista));
    dono.set(ultimo(lista), null);
  }
  return { texto: doc.toString(), sumiu: todos ? 'nula' : 'nada' };
}

// Ajusta um ponteiro depois de tirar `n` itens a partir de `de` da lista (`null`: ele saiu junto).
function depoisDeTirar(ponteiro, lista, de, n, sumiu) {
  if (ponteiro === null || ponteiro === undefined) return ponteiro;
  if (sumiu === 'corrente') {
    const k = Number(partes(lista)[1]);
    const m = String(ponteiro).match(new RegExp(`^/${SOLTOS}/(\\d+)(/.*)?$`));
    if (!m) return ponteiro;
    const j = Number(m[1]);
    if (j === k) return null;
    return j > k ? `/${SOLTOS}/${j - 1}${m[2] || ''}` : ponteiro;
  }
  if (ponteiro === lista) return sumiu === 'lista' ? null : ponteiro;
  if (!String(ponteiro).startsWith(`${lista}/`)) return ponteiro;
  if (sumiu === 'lista') return null;
  const [indice, ...resto] = ponteiro.slice(lista.length + 1).split('/');
  const j = Number(indice);
  if (j < de) return ponteiro;
  if (j < de + n) return null;
  return [`${lista}/${j - n}`, ...resto].join('/');
}

// Põe os blocos na posição `indice` da lista. A lista que ainda não existe nasce no passo dono
// dela (o `else`, o `default`, a rota de erro); a nula (`then:`) recebe os itens.
function porBlocos(base, lista, indice, blocos) {
  const doc = ler(base);
  const seq = noEm(doc, lista);

  if (emBloco(base, seq)) {
    const recuo = recuoDoTraco(base, seq.items[0]);
    const branco = separaComLinhaEmBranco(base, seq) ? '\n' : '';
    const texto = blocos.map(b => indentar(b, recuo)).join(branco);
    if (indice < seq.items.length) {
      const { comeco } = limitesDoItem(base, seq.items[indice]);
      return base.slice(0, comeco) + texto + branco + base.slice(comeco);
    }
    const { fim } = limitesDoItem(base, seq.items[seq.items.length - 1]);
    return base.slice(0, fim) + branco + texto + base.slice(fim);
  }

  const campo = ultimo(lista);
  const dono = noEm(doc, paiDe(lista));

  // A rota de erro que não existe (ou que é `continue`): o `on_error:` nasce com o `steps:`.
  if (ehRota(lista) && !isMap(dono)) {
    const passo = noEm(doc, paiDe(paiDe(lista)));
    if (isMap(passo) && !passo.flow && passo.items.length) {
      const par = parDe(passo, ROTA);
      const irmao = par || passo.items[passo.items.length - 1];
      const linhaDoIrmao = base.slice(
        inicioDaLinha(base, irmao.key.range[0]),
        irmao.key.range[0]
      );
      const recuo = ' '.repeat(
        naLinhaDoTraco(base, irmao)
          ? linhaDoIrmao.indexOf('-') + 2
          : linhaDoIrmao.length
      );
      const bloco = `${recuo}${ROTA}:\n${recuo}  steps:\n${blocos
        .map(b => indentar(b, `${recuo}    `))
        .join('')}`;
      if (par) {
        const { inicio, fim } = limitesDoPar(base, par);
        const escrito = naLinhaDoTraco(base, par)
          ? bloco.slice(recuo.length)
          : bloco;
        return base.slice(0, inicio) + escrito + base.slice(fim);
      }
      const { fim } = limitesDoPar(base, irmao);
      return base.slice(0, fim) + bloco + base.slice(fim);
    }
  }

  if (seq === undefined && isMap(dono) && !dono.flow && dono.items.length) {
    const recuo = ' '.repeat(
      dono.range[0] - inicioDaLinha(base, dono.range[0])
    );
    const ultimoPar = dono.items[dono.items.length - 1];
    const { fim } = limitesDoPar(base, ultimoPar);
    const bloco = `${recuo}${campo}:\n${blocos
      .map(b => indentar(b, `${recuo}  `))
      .join('')}`;
    return base.slice(0, fim) + bloco + base.slice(fim);
  }

  // A lista nula (`then:` sem item): os itens entram na linha de baixo da chave.
  const par = isMap(dono) && !dono.flow && parDe(dono, campo);
  const fimDaChave = par && base.indexOf('\n', par.key.range[1]);
  if (
    par &&
    (par.value === null ||
      (isScalar(par.value) &&
        par.value.value === null &&
        par.value.range[1] <= fimDaChave))
  ) {
    const recuo = ' '.repeat(
      par.key.range[0] - inicioDaLinha(base, par.key.range[0])
    );
    const fimDaLinha = fimDaChave < 0 ? base.length : fimDaChave + 1;
    let antes = base.slice(0, fimDaLinha);
    if (par.value?.range && par.value.range[1] > par.value.range[0]) {
      const [de, ate] = par.value.range;
      antes =
        base.slice(0, de).replace(/[ \t]+$/, '') + base.slice(ate, fimDaLinha);
    }
    return (
      comFimDeLinha(antes) +
      blocos.map(b => indentar(b, `${recuo}  `)).join('') +
      base.slice(fimDaLinha)
    );
  }

  // Estilo fluxo, ou o dono em fluxo: pela Document API, com o texto todo reescrito.
  const valores = valoresDosBlocos(blocos);
  if (isSeq(seq)) {
    seq.items.splice(indice, 0, ...valores.map(v => doc.createNode(v)));
    return doc.toString();
  }
  if (ehRota(lista)) {
    noEm(doc, paiDe(paiDe(lista))).set(ROTA, { steps: valores });
    return doc.toString();
  }
  dono.set(campo, doc.createNode(valores));
  return doc.toString();
}

// Uma corrente solta nova, no fim do `detached` (que nasce no fim do documento). Volta o texto e o
// índice da corrente.
function novaCorrente(base, blocos) {
  const doc = ler(base);
  const raiz = doc.contents;
  const soltos = isMap(raiz) ? raiz.get(SOLTOS, true) : null;
  if (emBloco(base, soltos)) {
    const recuo = recuoDoTraco(base, soltos.items[0]);
    const branco = separaComLinhaEmBranco(base, soltos) ? '\n' : '';
    const texto = `${recuo}- steps:\n${blocos
      .map(b => indentar(b, `${recuo}    `))
      .join('')}`;
    const { fim } = limitesDoItem(base, soltos.items[soltos.items.length - 1]);
    return {
      texto: base.slice(0, fim) + branco + texto + base.slice(fim),
      corrente: soltos.items.length,
    };
  }
  if (isMap(raiz) && !raiz.flow && !parDe(raiz, SOLTOS)) {
    const separador = /\n[ \t]*\n$/.test(base) || !base.trim() ? '' : '\n';
    return {
      texto: `${base}${separador}${SOLTOS}:\n  - steps:\n${blocos
        .map(b => indentar(b, '      '))
        .join('')}`,
      corrente: 0,
    };
  }
  const valores = valoresDosBlocos(blocos);
  if (isSeq(soltos)) {
    soltos.items.push(doc.createNode({ steps: valores }));
    return { texto: doc.toString(), corrente: soltos.items.length - 1 };
  }
  raiz.set(SOLTOS, doc.createNode([{ steps: valores }]));
  return { texto: doc.toString(), corrente: 0 };
}

// -- ligar: mover Y para logo depois de X (ou para o começo de uma saída de X)

const indiceNaLista = ponteiro => Number(ultimo(ponteiro));

// O passo dono de uma lista (o `if` do `then`, o laço do corpo, o passo da rota de erro, o Router
// do ramo); `null` para os `steps` do flow e para a corrente solta.
export function donoDaLista(lista) {
  if (lista === '/steps' || ehCorrente(lista)) return null;
  if (ehRota(lista)) return paiDe(paiDe(lista));
  const p = partes(lista);
  if (p[p.length - 3] === 'branches') return paiDe(paiDe(paiDe(lista)));
  if (p[p.length - 2] === 'cases') return paiDe(paiDe(lista));
  return paiDe(lista);
}

// O passo que vem logo antes do ponto de inserção (o X do gesto): o de cima na mesma lista, ou o dono
// da lista quando o ponto é o começo dela; `null`, o gatilho.
export function passoAntesDe({ lista, indice }) {
  if (indice > 0) return `${lista}/${indice - 1}`;
  return donoDaLista(lista);
}

const recusa = (codigo, extra = {}) => ({ recusa: { codigo, ...extra } });

// Ligar a bolinha de saída de X ao passo Y (R18): Y sai de onde está (com o que tem dentro) e entra
// em `destino`, o lugar que a saída de X dá: logo depois de X na lista dele, ou o começo da lista
// daquela saída. Y numa corrente solta leva junto o que vem depois dele na corrente.
// Recusas (SPEC, "Gestos de conexão"): `dentro` (Y contém X), `solto` (X está solto e Y no flow),
// `voltar` (Y roda antes de X naquele caminho; `forcar` é o botão "Mover y para depois de x") e
// `vazia` (Y é o único passo de uma lista que não fica vazia). Y já no lugar: `{nada: true}`.
export function moverPasso(texto, ponteiroY, destino, { forcar = false } = {}) {
  const base = comFimDeLinha(texto);
  const doc = ler(base);
  const passo = noEm(doc, ponteiroY);
  const x = passoAntesDe(destino);
  if (!isMap(passo)) return recusa('sumiu', { y: ponteiroY, x });
  const listaY = paiDe(ponteiroY);
  const iY = indiceNaLista(ponteiroY);
  const seqY = noEm(doc, listaY);

  if (
    destino.lista === listaY &&
    (destino.indice === iY || destino.indice === iY + 1)
  ) {
    return { texto, ponteiro: ponteiroY, nada: true };
  }
  if (`${destino.lista}/`.startsWith(`${ponteiroY}/`)) {
    return recusa('dentro', { y: ponteiroY, x });
  }
  if (estaSolto(destino.lista) && !estaSolto(ponteiroY)) {
    return recusa('solto', { y: ponteiroY, x });
  }
  const alvo = `${destino.lista}/${destino.indice}`;
  if (!forcar && alvo.startsWith(`${listaY}/`)) {
    const j = Number(alvo.slice(listaY.length + 1).split('/')[0]);
    const antes = destino.lista === listaY ? iY < j - 1 : iY < j;
    if (antes) return recusa('voltar', { y: ponteiroY, x });
  }
  const corrente = ehCorrente(listaY);
  if (!corrente && !opcional(listaY) && seqY.items.length === 1) {
    return recusa('vazia', { y: ponteiroY, lista: listaY });
  }

  const ate = corrente ? seqY.items.length : iY + 1;
  const blocos = trechoDaLista(base, doc, listaY, iY, ate);
  const tirado = tirarItens(base, listaY, iY, ate);
  const lista = depoisDeTirar(
    destino.lista,
    listaY,
    iY,
    ate - iY,
    tirado.sumiu
  );
  // O índice no destino, se ele está na mesma lista e depois dos que saíram.
  const indice =
    destino.lista === listaY && destino.indice > iY
      ? destino.indice - (ate - iY)
      : destino.indice;
  return {
    texto: porBlocos(tirado.texto, lista, indice, blocos),
    ponteiro: `${lista}/${indice}`,
  };
}

// -- apagar uma conexão: o trecho de baixo vira uma corrente solta (R19)

// `apagar` é o que a conexão diz (`grafoDoFlow`, `data.apagar`):
// - `{tipo: 'trecho', lista, indice}`: entre dois passos da mesma lista; o de baixo e os que vêm
//   depois dele viram uma corrente solta;
// - `{tipo: 'lista', lista, obrigatoria, gatilho}`: do bloco ao primeiro passo de uma lista; a lista
//   opcional sai inteira (e a chave), e a obrigatória é recusada;
// - `{recusa, bloco, tipo}`: a junção dos ramos e as conexões do laço, que são do bloco.
export function soltarTrecho(texto, apagar) {
  if (apagar?.recusa) return recusa(apagar.recusa, { ...apagar });
  if (apagar?.tipo === 'lista' && apagar.obrigatoria) {
    return recusa(apagar.gatilho ? 'gatilho' : 'obrigatoria', {
      lista: apagar.lista,
    });
  }
  const base = comFimDeLinha(texto);
  const doc = ler(base);
  const seq = noEm(doc, apagar.lista);
  if (!isSeq(seq)) return { texto, ponteiro: null };
  const de = apagar.tipo === 'trecho' ? apagar.indice : 0;
  const blocos = trechoDaLista(base, doc, apagar.lista, de, seq.items.length);
  const tirado = tirarItens(base, apagar.lista, de, seq.items.length);
  const { texto: novo, corrente } = novaCorrente(tirado.texto, blocos);
  return { texto: novo, ponteiro: null, corrente };
}

// Religar (arrastar a ponta de chegada de X→Z até Y): solta Z e o que vem depois dele, como apagar,
// e liga X a Y, num gesto só. Vale a recusa do resultado final, e não a do meio: religar a primeira
// do `then` para Y é válido, porque o `then` termina com Y.
export function religar(texto, apagar, ponteiroY, { forcar = false } = {}) {
  if (apagar?.recusa) return recusa(apagar.recusa, { ...apagar });
  const destino =
    apagar.tipo === 'trecho'
      ? { lista: apagar.lista, indice: apagar.indice }
      : { lista: apagar.lista, indice: 0 };
  if (ponteiroY === `${destino.lista}/${destino.indice}`) {
    return { texto, ponteiro: ponteiroY, nada: true };
  }
  const base = comFimDeLinha(texto);
  const doc = ler(base);
  const seq = noEm(doc, destino.lista);
  if (!isSeq(seq)) return { texto, ponteiro: ponteiroY, nada: true };
  const de = destino.indice;
  const blocos = trechoDaLista(base, doc, destino.lista, de, seq.items.length);
  const tirado = tirarItens(base, destino.lista, de, seq.items.length);
  const { texto: solto, corrente } = novaCorrente(tirado.texto, blocos);
  // Y estava no trecho que soltou: agora mora na corrente nova.
  let y = ponteiroY;
  if (y.startsWith(`${destino.lista}/`)) {
    const [indice, ...resto] = y.slice(destino.lista.length + 1).split('/');
    if (Number(indice) >= de) {
      y = [
        `/${SOLTOS}/${corrente}/steps/${Number(indice) - de}`,
        ...resto,
      ].join('/');
    }
  }
  const movido = moverPasso(solto, y, destino, { forcar });
  if (movido.recusa) return movido;
  if (movido.nada) return { texto, ponteiro: ponteiroY, nada: true };
  return movido;
}

// -- renomear (R21)

const textoDoNo = no => (isScalar(no) ? no.value : undefined);

// As trocas de nomes em todos os passos do documento (os do flow e os soltos).
function trocasNoDocumento(base, doc, mapa) {
  const raiz = doc.contents;
  if (!isMap(raiz)) return [];
  const trocas = trocasNosPassos(base, raiz.get('steps', true), mapa);
  const soltos = raiz.get(SOLTOS, true);
  if (isSeq(soltos)) {
    soltos.items.forEach(corrente => {
      if (isMap(corrente)) {
        trocas.push(
          ...trocasNosPassos(base, corrente.get('steps', true), mapa)
        );
      }
    });
  }
  return trocas;
}

// Os passos `code` cujo JavaScript pode citar o nome: o texto do código não é reescrito, e a tela
// avisa ("o código de `normaliza` pode citar `buscar`; confira").
function codigosQueCitam(doc, nome) {
  const quem = [];
  const palavra = new RegExp(`(^|[^A-Za-z0-9_$.])${nome}(?![A-Za-z0-9_$])`);
  percorrer(doc, (passo, ponteiro) => {
    const codigo = passo.get('code');
    if (passo.get('type') === 'code' && typeof codigo === 'string') {
      if (palavra.test(codigo)) quem.push(ponteiro);
    }
  });
  return quem;
}

// Uma chave nova no topo do passo (o `id`, a `description`), antes da primeira: é onde se lê o nome
// de um passo. `depois`: a chave que fica antes dela, quando existe.
function chaveNoTopo(base, ponteiro, chave, valor, depois = null) {
  const doc = ler(base);
  const passo = noEm(doc, ponteiro);
  if (!isMap(passo) || passo.flow || !passo.items.length) {
    passo.set(chave, valor);
    return doc.toString();
  }
  const coluna = par =>
    ' '.repeat(par.key.range[0] - inicioDaLinha(base, par.key.range[0]));
  const ancora = depois && parDe(passo, depois);
  if (ancora) {
    const { fim } = limitesDoPar(base, ancora);
    return `${base.slice(0, fim)}${coluna(ancora)}${chave}: ${escalar(valor)}\n${base.slice(fim)}`;
  }
  const primeira = passo.items[0];
  const inicio = primeira.key.range[0];
  return `${base.slice(0, inicio)}${chave}: ${escalar(valor)}\n${coluna(primeira)}${base.slice(inicio)}`;
}

// Renomear o passo (F2): o título (`description`) e o nome nas fórmulas (`id`). Mudar o `id`
// reescreve, no mesmo gesto, toda fórmula do flow que o lê, pelo leitor de CEL (`nomesNasFormulas`).
// Recusas no campo: `invalido`, `reservado` e `repetido`. `codigos`: os passos `code` que podem
// citar o nome velho.
export function renomearPasso(texto, ponteiro, { id, descricao } = {}) {
  const base = comFimDeLinha(texto);
  const doc = ler(base);
  const passo = noEm(doc, ponteiro);
  if (!isMap(passo)) return { texto, ponteiro, codigos: [] };
  const atual = textoDoNo(passo.get('id', true)) ?? '';
  const novoId = id === undefined ? atual : String(id).trim();
  let resultado = base;
  let codigos = [];
  if (novoId !== atual) {
    if (novoId && !ID_VALIDO.test(novoId))
      return recusa('invalido', { campo: 'id' });
    if (RESERVADOS.has(novoId)) return recusa('reservado', { campo: 'id' });
    if (novoId && idsDoDocumento(doc).has(novoId)) {
      return recusa('repetido', { campo: 'id' });
    }
    if (atual && novoId) {
      resultado = aplicarTrocas(
        base,
        trocasNoDocumento(base, doc, { [atual]: novoId })
      );
      codigos = codigosQueCitam(doc, atual);
    }
    if (!novoId) {
      resultado = definirValor(resultado, ponteiro, ['id'], undefined).texto;
    } else if (atual) {
      resultado = definirValor(resultado, ponteiro, ['id'], novoId).texto;
    } else {
      resultado = chaveNoTopo(resultado, ponteiro, 'id', novoId);
    }
  }
  const descricaoAtual = textoDoNo(passo.get('description', true)) ?? '';
  const novaDescricao =
    descricao === undefined ? descricaoAtual : String(descricao).trim();
  if (novaDescricao !== descricaoAtual) {
    const temDescricao = descricaoAtual !== '' || passo.has('description');
    if (!novaDescricao) {
      resultado = definirValor(
        resultado,
        ponteiro,
        ['description'],
        undefined
      ).texto;
    } else if (temDescricao) {
      resultado = definirValor(
        resultado,
        ponteiro,
        ['description'],
        novaDescricao
      ).texto;
    } else {
      resultado = chaveNoTopo(
        resultado,
        ponteiro,
        'description',
        novaDescricao,
        'id'
      );
    }
  }
  return { texto: resultado === base ? texto : resultado, ponteiro, codigos };
}

// -- substituir (R21)

// Cada passo de uma lista e de dentro dela, como o `percorrer` faz no documento.
function percorrerLista(doc, lista, aoPasso) {
  const seq = noEm(doc, lista);
  if (!isSeq(seq)) return;
  seq.items.forEach((passo, i) => {
    if (!isMap(passo)) return;
    const ponteiro = `${lista}/${i}`;
    aoPasso(passo, ponteiro);
    sublistas(passo, ponteiro).forEach(sub =>
      percorrerLista(doc, sub.ponteiro, aoPasso)
    );
  });
}

// As listas de passos de um passo, pelo nome do campo, para o Substituir saber o que sobra.
function listasDoPasso(passo, ponteiro) {
  return sublistas(passo, ponteiro).map(sub => sub.ponteiro);
}

// Substituir o tipo do passo (R): ficam `id`, `description`, `note`, `disabled` e os campos que o
// tipo novo também aceita (`on_error`, `retry`, `timeout`); o resto vem do modelo do tipo novo.
// `for_each` e `while` trocam entre si mantendo o corpo. As listas de dentro que o tipo novo não tem
// viram correntes soltas, e `soltos` diz quantos passos ficaram soltos (nada some calado).
export function substituirPasso(texto, ponteiro, tipo) {
  const base = comFimDeLinha(texto);
  const doc = ler(base);
  const passo = noEm(doc, ponteiro);
  if (!isMap(passo)) return { texto, ponteiro, soltos: 0 };
  const antigo = passo.toJSON();
  if (antigo.type === tipo) return { texto, ponteiro, soltos: 0 };

  const modelo = modeloDoPasso(tipo, doc);
  const novo = {};
  if (antigo.id !== undefined) novo.id = antigo.id;
  else if (modelo.id) novo.id = modelo.id;
  ['description', 'note'].forEach(campo => {
    if (antigo[campo] !== undefined) novo[campo] = antigo[campo];
  });
  const guardaCorpo = LACOS.includes(antigo.type) && LACOS.includes(tipo);
  Object.entries(modelo).forEach(([campo, valor]) => {
    if (campo === 'id') return;
    if (campo === 'steps' && guardaCorpo) return;
    novo[campo] = valor;
  });
  if (antigo.disabled !== undefined) novo.disabled = antigo.disabled;
  Object.entries(CAMPOS_GUARDADOS).forEach(([campo, tipos]) => {
    if (antigo[campo] !== undefined && tipos.includes(tipo)) {
      novo[campo] = antigo[campo];
    }
  });
  const rota = passo.get(ROTA, true);
  const temRota = isMap(rota) && isSeq(rota.get('steps', true));
  if (typeof antigo.on_error === 'string') novo.on_error = antigo.on_error;

  // O que fica (o corpo do laço, a rota de erro) vai como texto, com os comentários; o resto das
  // listas de dentro vira corrente solta.
  const guardadas = [
    ...(guardaCorpo ? [`${ponteiro}/steps`] : []),
    ...(temRota ? [`${ponteiro}/${ROTA}/steps`] : []),
  ];
  const soltas = listasDoPasso(passo, ponteiro).filter(
    lista => !guardadas.includes(lista)
  );
  const blocosDe = lista => {
    const seq = noEm(doc, lista);
    return isSeq(seq)
      ? trechoDaLista(base, doc, lista, 0, seq.items.length)
      : [];
  };
  let soltos = 0;
  soltas.forEach(lista => {
    percorrerLista(doc, lista, () => {
      soltos += 1;
    });
  });

  const lista = paiDe(ponteiro);
  const seq = noEm(doc, lista);
  let resultado;
  if (emBloco(base, seq)) {
    const item = seq.items[indiceNaLista(ponteiro)];
    const { inicio, fim } = limitesDoItem(base, item);
    const recuo = recuoDoTraco(base, item);
    let escrito = textoDoItem(novo, recuo);
    if (guardaCorpo) {
      escrito += `${recuo}  steps:\n${blocosDe(`${ponteiro}/steps`)
        .map(b => indentar(b, `${recuo}    `))
        .join('')}`;
    }
    if (temRota) {
      escrito += `${recuo}  ${ROTA}:\n${recuo}    steps:\n${blocosDe(
        `${ponteiro}/${ROTA}/steps`
      )
        .map(b => indentar(b, `${recuo}      `))
        .join('')}`;
    }
    resultado = base.slice(0, inicio) + escrito + base.slice(fim);
  } else {
    if (guardaCorpo) novo.steps = antigo.steps;
    if (temRota) novo[ROTA] = antigo[ROTA];
    seq.items[indiceNaLista(ponteiro)] = doc.createNode(novo);
    resultado = doc.toString();
  }
  soltas.forEach(listaSolta => {
    const blocos = blocosDe(listaSolta);
    if (blocos.length) resultado = novaCorrente(resultado, blocos).texto;
  });
  return { texto: resultado, ponteiro, soltos };
}

// -- copiar, colar e duplicar (R21)

// A posição do passo no texto, para a ordem do flow.
const posicaoNoTexto = (doc, ponteiro) => noEm(doc, ponteiro)?.range?.[0] ?? 0;

// Os ponteiros sem os que estão dentro de outro da lista, na ordem do flow.
function principais(doc, ponteiros) {
  const unicos = [...new Set(ponteiros.filter(Boolean))];
  return unicos
    .filter(
      p => !unicos.some(outro => outro !== p && p.startsWith(`${outro}/`))
    )
    .filter(p => isMap(noEm(doc, p)))
    .sort((a, b) => posicaoNoTexto(doc, a) - posicaoNoTexto(doc, b));
}

// Copiar (Ctrl/⌘+C): o YAML dos passos selecionados, com o que está dentro, na ordem do flow, como
// uma lista de passos: cola noutro flow, aqui mesmo ou no código.
export function copiarPassos(texto, ponteiros) {
  const base = comFimDeLinha(texto);
  const doc = ler(base);
  return principais(doc, ponteiros)
    .map(
      p =>
        blocosDaLista(
          base,
          doc,
          paiDe(p),
          indiceNaLista(p),
          indiceNaLista(p) + 1
        )[0]
    )
    .join('');
}

// Os tipos de todos os passos de uma lista de valores, com os de dentro.
function tiposDosPassos(passos, aoPasso) {
  (passos || []).forEach(passo => {
    if (!passo || typeof passo !== 'object' || Array.isArray(passo)) {
      aoPasso(null);
      return;
    }
    aoPasso(passo);
    ['then', 'else', 'default', 'steps'].forEach(campo => {
      if (Array.isArray(passo[campo])) tiposDosPassos(passo[campo], aoPasso);
    });
    if (passo.cases && typeof passo.cases === 'object') {
      Object.values(passo.cases).forEach(caso => {
        if (Array.isArray(caso)) tiposDosPassos(caso, aoPasso);
      });
    }
    if (Array.isArray(passo.branches)) {
      passo.branches.forEach(ramo => tiposDosPassos(ramo?.steps, aoPasso));
    }
    if (passo.on_error && Array.isArray(passo.on_error.steps)) {
      tiposDosPassos(passo.on_error.steps, aoPasso);
    }
  });
}

// O mapa raiz em bloco como item de lista (`- ` na primeira linha dele), com os comentários.
function mapaComoItem(texto, mapa) {
  const inicio = inicioDaLinha(texto, mapa.range[0]);
  const antes = texto.slice(0, inicio);
  const linhas = texto.slice(inicio).trimEnd().split('\n');
  return comFimDeLinha(
    antes +
      linhas.map((l, i) => (i === 0 ? `- ${l}` : l && `  ${l}`)).join('\n')
  );
}

// Os blocos do que veio da área de transferência: uma lista de passos, um passo só, ou um flow
// inteiro (os `steps` dele). `{blocos}` ou `{recusa}`.
function blocosColados(colado) {
  const fonte = comFimDeLinha(String(colado ?? '').replace(/\r\n?/g, '\n'));
  const doc = parseDocument(fonte);
  if (doc.errors.length || !doc.contents) return recusa('nao_e_passo');
  const raiz = doc.contents;
  const valor = raiz.toJSON?.();
  if (
    valor &&
    typeof valor === 'object' &&
    Array.isArray(valor.nodes) &&
    valor.connections &&
    typeof valor.connections === 'object'
  ) {
    return recusa('n8n');
  }
  let blocos;
  if (isSeq(raiz)) {
    blocos = blocosDaLista(fonte, doc, '', 0, raiz.items.length);
  } else if (isMap(raiz) && raiz.get('type') !== undefined) {
    blocos = raiz.flow ? [textoDoItem(valor, '')] : [mapaComoItem(fonte, raiz)];
  } else if (isMap(raiz) && isSeq(raiz.get('steps', true))) {
    blocos = blocosDaLista(
      fonte,
      doc,
      '/steps',
      0,
      raiz.get('steps', true).items.length
    );
  } else {
    return recusa('nao_e_passo');
  }
  const passos = valoresDosBlocos(blocos);
  let valido = passos.length > 0;
  tiposDosPassos(passos, passo => {
    if (!passo || !TIPOS_DE_PASSO.includes(passo.type)) valido = false;
  });
  return valido ? { blocos, passos } : recusa('nao_e_passo');
}

// O primeiro nome livre (`buscar_2`, `buscar_3`…).
const nomeLivre = (ocupados, nome) => {
  let n = 2;
  while (ocupados.has(`${nome}_${n}`)) n += 1;
  return `${nome}_${n}`;
};

// Os blocos com os ids que já existem no flow trocados pelo primeiro sufixo livre, e as fórmulas do
// colado que liam o id velho lendo o novo. Referência a passo que não existe neste flow fica.
function renomearColados(blocos, existentes) {
  const fonte = blocos.join('');
  const doc = parseDocument(fonte);
  const ids = [];
  const coletar = seq =>
    isSeq(seq) &&
    seq.items.forEach((passo, i) => {
      if (!isMap(passo)) return;
      const id = passo.get('id', true);
      if (isScalar(id) && typeof id.value === 'string') ids.push(id);
      sublistas(passo, String(i)).forEach(sub => coletar(sub.no));
    });
  coletar(doc.contents);
  const ocupados = new Set([...existentes, ...ids.map(no => no.value)]);
  const mapa = {};
  ids.forEach(no => {
    if (!existentes.has(no.value) || mapa[no.value]) return;
    const novo = nomeLivre(ocupados, no.value);
    ocupados.add(novo);
    mapa[no.value] = novo;
  });
  if (!Object.keys(mapa).length) return { blocos, mapa };
  const trocas = [
    ...trocasNosPassos(fonte, doc.contents, mapa),
    ...ids
      .filter(no => mapa[no.value])
      .map(no => ({
        inicio: no.range[0],
        fim: no.range[1],
        texto: mapa[no.value],
      })),
  ];
  const novo = aplicarTrocas(fonte, trocas);
  const lido = parseDocument(novo);
  return {
    blocos: blocosDaLista(novo, lido, '', 0, lido.contents.items.length),
    mapa,
  };
}

// Colar (Ctrl/⌘+V): o que veio da área de transferência entra como corrente logo depois do passo
// selecionado (`depoisDe`), ou, sem seleção, como corrente solta. Texto que começa com `curl` vira
// um passo `http` (`curlParaPassoHttp`), com as credenciais à parte. `foraDaCamada(tipo)`: o tipo que
// a camada de quem cola não aceita (S4) não cola nada. Recusas: `nao_e_passo`, `n8n`, `curl` (com o
// erro do leitor) e `camada` (com o tipo).
export function colarPassos(
  texto,
  colado,
  { depoisDe = null, foraDaCamada = () => false, nomeSensivel } = {}
) {
  const base = comFimDeLinha(texto);
  const doc = ler(base);
  const existentes = idsDoDocumento(doc);
  let blocos;
  let passos;
  let extra = {};
  if (/^\s*curl(\s|$)/i.test(String(colado ?? ''))) {
    const lido = curlParaPassoHttp(colado, { nomeSensivel, ids: existentes });
    if (!lido.ok) return recusa('curl', { erro: lido.erro });
    passos = [lido.passo];
    blocos = [textoDoItem(lido.passo, '')];
    extra = { credenciais: lido.credenciais, avisos: lido.avisos, curl: true };
  } else {
    const lido = blocosColados(colado);
    if (lido.recusa) return lido;
    ({ blocos, passos } = lido);
  }
  let barrado = null;
  tiposDosPassos(passos, passo => {
    if (!barrado && passo && foraDaCamada(passo.type)) barrado = passo.type;
  });
  if (barrado) return recusa('camada', { tipo: barrado });
  const renomeados = renomearColados(blocos, existentes);

  if (depoisDe && isMap(noEm(doc, depoisDe))) {
    const lista = paiDe(depoisDe);
    const indice = indiceNaLista(depoisDe) + 1;
    return {
      texto: porBlocos(base, lista, indice, renomeados.blocos),
      ponteiro: `${lista}/${indice}`,
      colados: renomeados.blocos.length,
      renomeados: renomeados.mapa,
      ...extra,
    };
  }
  const { texto: novo, corrente } = novaCorrente(base, renomeados.blocos);
  return {
    texto: novo,
    ponteiro: `/${SOLTOS}/${corrente}/steps/0`,
    corrente,
    colados: renomeados.blocos.length,
    renomeados: renomeados.mapa,
    ...extra,
  };
}

// Duplicar (Ctrl/⌘+D): copia e cola logo depois do selecionado; com vários, depois do último deles.
export function duplicarPassos(texto, ponteiros) {
  const doc = ler(comFimDeLinha(texto));
  const selecionados = principais(doc, ponteiros);
  if (!selecionados.length) return { texto, ponteiro: null };
  const ultimoSelecionado = selecionados[selecionados.length - 1];
  return colarPassos(texto, copiarPassos(texto, selecionados), {
    depoisDe: ultimoSelecionado,
  });
}

// Excluir vários (Delete com vários selecionados, e o Recortar): do último para o primeiro, cada um
// como o `excluirPasso` (o último de uma lista obrigatória fica).
export function excluirPassos(texto, ponteiros) {
  const doc = ler(comFimDeLinha(texto));
  return principais(doc, ponteiros)
    .reverse()
    .reduce((atual, ponteiro) => excluirPasso(atual.texto, ponteiro), {
      texto,
      ponteiro: null,
    });
}

// Desativar / Ativar (D): grava ou tira `disabled: true` (R20). Com vários, se todos já estão
// desativados, ativa todos; senão, desativa os que faltam, como o n8n. O rótulo do menu de vários lê
// o mesmo critério (`selecaoDesativada`).
const todosDesativados = (doc, selecionados) =>
  selecionados.every(p => noEm(doc, p).get('disabled') === true);

export function alternarDesativado(texto, ponteiros) {
  const doc = ler(comFimDeLinha(texto));
  const selecionados = principais(doc, ponteiros);
  if (!selecionados.length) return { texto, ponteiro: null };
  const todos = todosDesativados(doc, selecionados);
  const resultado = [...selecionados].reverse().reduce((atual, p) => {
    const passo = noEm(ler(comFimDeLinha(atual)), p);
    if (todos) return definirValor(atual, p, ['disabled'], undefined).texto;
    if (passo.get('disabled') === true) return atual;
    return definirCampo(atual, p, 'disabled', true).texto;
  }, texto);
  return { texto: resultado, ponteiro: selecionados[0], desativado: !todos };
}

// FLOW-11 (3ª rodada do Gate 4): o que o D faria nestes passos, para o rótulo do menu de vários.
// `true` quando todos já estão desativados, e o gesto os ativa ("Ativar"); com algum ativo, `false`,
// e o gesto desativa todos ("Desativar"). Só lê: usa o documento guardado da leitura.
export function selecaoDesativada(texto, ponteiros) {
  const { doc } = documentoLido(texto);
  const selecionados = principais(doc, ponteiros);
  return selecionados.length > 0 && todosDesativados(doc, selecionados);
}

// Os ids do flow (com os soltos): o que o renomear e o colar conferem.
export const idsDoFlow = texto => idsDoDocumento(documentoLido(texto).doc);
